import { useState, useEffect } from 'react';
import { Skeleton } from '../components/Skeleton';

const API = '/api/settings';

const TZ_OPTIONS = [
  { value: 'Asia/Dubai',    label: 'Dubai (GST, UTC+4)',       cron: '0 4 * * *' },
  { value: 'Europe/Zurich', label: 'Zurich (CET/CEST, UTC+1/+2)', cron: '0 7 * * * (winter) / 0 6 * * * (summer)' },
  { value: 'Asia/Bangkok',  label: 'Bangkok (ICT, UTC+7)',     cron: '0 1 * * *' },
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [propertyNames, setPropertyNames] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(API)
      .then(r => r.json())
      .then(setSettings)
      .catch(e => setError(e.message));
    fetch('/api/properties/names')
      .then(r => r.json())
      .then(names => setPropertyNames(Array.isArray(names) ? names : []))
      .catch(() => {});
  }, []);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSettings(data.settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!settings) {
    return (
      <div className="p-6 max-w-xl space-y-6">
        <Skeleton className="h-8 w-40 mb-6" />
        {[1, 2].map((section) => (
          <div key={section} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="grid grid-cols-2 gap-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>
        ))}
      </div>
    );
  }

  const selectedTz = TZ_OPTIONS.find(o => o.value === settings.briefingTz) || TZ_OPTIONS[0];

  return (
    <div className="p-6 max-w-xl">
      <h1 className="text-2xl font-bold mb-6">Settings</h1>

      {/* Morning Briefing */}
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="text-lg font-semibold mb-1">☀️ Morning Briefing</h2>
        <p className="text-sm text-gray-500 mb-4">
          Delivered daily at <strong>08:00 local time</strong> in the selected timezone via Telegram.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
        <select
          value={settings.briefingTz}
          onChange={e => setSettings({ ...settings, briefingTz: e.target.value })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3"
        >
          {TZ_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <p className="text-xs text-gray-400">
          Cron schedule (UTC): <code className="bg-gray-100 px-1 rounded">{selectedTz.cron}</code>
        </p>
      </section>

      {/* Invoice Settings */}
      <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <h2 className="text-lg font-semibold mb-1">📄 Invoice settings</h2>
        <p className="text-sm text-gray-500 mb-4">
          Sender block and SMTP settings used for PDF invoices.
        </p>

        <label className="block text-sm font-medium text-gray-700 mb-1">
          Default sender block (used as "From" when a property has no sender below)
        </label>
        <textarea
          rows={5}
          value={settings.invoice?.senderBlock ?? ''}
          onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, senderBlock: e.target.value } })}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4 resize-none"
          placeholder={"Your Name\n+49 123 456789\nStreet, City, Country\nyou@example.com"}
        />

        {propertyNames.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Per-property sender (overrides the default for that property's invoices)
            </label>
            <div className="space-y-3">
              {propertyNames.map(name => (
                <div key={name}>
                  <div className="text-xs font-medium text-gray-500 mb-1">{name}</div>
                  <textarea
                    rows={4}
                    value={settings.invoice?.senders?.[name] ?? ''}
                    onChange={e => setSettings({
                      ...settings,
                      invoice: {
                        ...settings.invoice,
                        senders: { ...(settings.invoice?.senders ?? {}), [name]: e.target.value },
                      },
                    })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Leave blank to use the default sender above"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SMTP host</label>
            <input
              type="text"
              value={settings.invoice?.smtp?.host ?? ''}
              onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, smtp: { ...settings.invoice?.smtp, host: e.target.value } } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="smtp.gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SMTP port</label>
            <input
              type="number"
              value={settings.invoice?.smtp?.port ?? 587}
              onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, smtp: { ...settings.invoice?.smtp, port: parseInt(e.target.value, 10) } } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mb-3">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={settings.invoice?.smtp?.secure ?? false}
              onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, smtp: { ...settings.invoice?.smtp, secure: e.target.checked } } })}
              className="rounded"
            />
            Use TLS (port 465)
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">SMTP user (email)</label>
            <input
              type="email"
              value={settings.invoice?.smtp?.user ?? ''}
              onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, smtp: { ...settings.invoice?.smtp, user: e.target.value } } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="you@gmail.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From name</label>
            <input
              type="text"
              value={settings.invoice?.smtp?.fromName ?? ''}
              onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, smtp: { ...settings.invoice?.smtp, fromName: e.target.value } } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your Name"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default currency</label>
            <select
              value={settings.invoice?.currency ?? 'EUR'}
              onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, currency: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {['EUR', 'USD', 'GBP', 'CHF', 'AED'].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Line item description</label>
            <input
              type="text"
              value={settings.invoice?.lineItemDescription ?? 'Accommodation'}
              onChange={e => setSettings({ ...settings, invoice: { ...settings.invoice, lineItemDescription: e.target.value } })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-2">
          Note: SMTP password is NOT stored in settings — it is read from the{' '}
          <code className="bg-gray-100 px-1 rounded">SMTP_PASS</code> environment variable on the server.
          Set it in your shell or in the server&apos;s launch script.
        </p>
      </section>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition"
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        {saved && <span className="text-green-600 text-sm">✓ Saved</span>}
        {error && <span className="text-red-500 text-sm">Error: {error}</span>}
      </div>
    </div>
  );
}
